// ─── Pricing (consistent with all previous epics) ────────────────────────────

export interface PricingTier {
  effective_price: number         // Price customer pays — server-resolved
  base_price: number              // Original / catalogue price
  compare_price: number | null    // Struck-through price (shown when discounted)
  discount_percentage: number | null
  price_source: 'flash_sale' | 'campaign' | 'product_discount' | 'base'
  campaign_ends_at: string | null // ISO datetime
  campaign_id: string | null
  vat_included: boolean
  currency: 'KES'
}

// ─── Cart Item ────────────────────────────────────────────────────────────────

export type CartItemStatus =
  | 'valid'
  | 'price_changed'       // Effective price differs from when item was added
  | 'out_of_stock'        // quantity > available stock
  | 'stock_reduced'       // Stock now lower than requested quantity (auto-capped)
  | 'product_removed'     // Product deleted from catalogue
  | 'campaign_expired'    // Campaign that drove the price has ended

export interface CartItem {
  /** Stable cart line ID (server for auth, UUID for guest) */
  id: string
  product_id: string
  variant_id?: string
  slug: string
  name: string
  brand: string
  image: string
  /** Quantity the customer has requested */
  quantity: number
  /** Maximum quantity allowed (= current stock, server-enforced) */
  max_quantity: number
  /** Pricing at time of last server validation */
  pricing: PricingTier
  /** Per-unit price — derived from pricing.effective_price */
  unit_price: number
  /** quantity × unit_price — derived, server-confirmed at checkout */
  subtotal: number
  /** Status of this line item — mutates on revalidation */
  status: CartItemStatus
  /** When this item was added — used for expiry checks */
  added_at: string
}

// ─── Cart Summary ─────────────────────────────────────────────────────────────

export interface CartSummary {
  /** Sum of all line item subtotals */
  subtotal: number
  /** Sum of all discounts (compare_price - effective_price) × qty */
  total_savings: number
  /** Shipping placeholder — KES 0 until address is confirmed */
  shipping_estimate: number
  /** VAT extracted from subtotal (if vat_included) */
  tax: number
  /** subtotal + shipping_estimate */
  total: number
  currency: 'KES'
  /** Count of distinct line items */
  item_count: number
  /** Total units across all lines */
  unit_count: number
}

// ─── Server API response shapes ───────────────────────────────────────────────

export interface ServerCartItem {
  id: string
  product_id: string
  variant_id?: string
  slug: string
  name: string
  brand: string
  image: string
  quantity: number
  max_quantity: number
  pricing: PricingTier
  status: CartItemStatus
  added_at: string
}

export interface ServerCartResponse {
  id: string                  // Cart session ID
  items: ServerCartItem[]
  summary: CartSummary
  last_validated_at: string
}

// ─── Add to cart request ──────────────────────────────────────────────────────

export interface AddToCartPayload {
  product_id: string
  variant_id?: string
  quantity: number
}

// ─── Update quantity request ──────────────────────────────────────────────────

export interface UpdateQuantityPayload {
  item_id: string
  quantity: number
}

// ─── Merge request (guest → auth) ────────────────────────────────────────────

export interface CartMergePayload {
  guest_items: Array<{
    product_id: string
    variant_id?: string
    quantity: number
  }>
  strategy: 'sum_quantities' | 'replace_with_guest' | 'keep_server'
}

// ─── Revalidation result ──────────────────────────────────────────────────────

export interface CartRevalidationResult {
  items: ServerCartItem[]
  summary: CartSummary
  changes: CartRevalidationChange[]
  has_invalid_items: boolean
}

export interface CartRevalidationChange {
  item_id: string
  type: CartItemStatus
  old_price?: number
  new_price?: number
  old_quantity?: number
  new_quantity?: number
}

// ─── Guest cart persistence ───────────────────────────────────────────────────

export interface PersistedGuestCart {
  version: number             // Schema version — bump on breaking changes
  items: CartItem[]
  persisted_at: string        // ISO datetime — used for expiry check
}

export const GUEST_CART_VERSION = 1
export const GUEST_CART_TTL_DAYS = 30
export const GUEST_CART_KEY = 'onetech_guest_cart'